import debounce from 'lodash.debounce';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import ImprovedSelect from './ImprovedSelect';
import PharmacyAvatar from '../../Pharmacy/PharmacyAvatar';
import CreatePharmacyModal from '../../Modal/CreatePharmacyModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../api';

const PAGE_SIZE = 15;

const InputPharmacy = ({
  name,
  onChange,
  onSelectPharmacy,
  errors,
  validationRules = {},
  allowCustomValue = false,
  className,
  disabled,
}) => {
  const { t } = useTranslation();
  const inputRef    = useRef(null);
  const dataMap     = useRef({});
  const { control, getValues } = useFormContext();

  const [options, setOptions]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [hasMore, setHasMore]   = useState(false);
  const [page, setPage]         = useState(0);
  const [keyword, setKeyword]   = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const loadingMore             = useRef(false);

  const fetchPage = async (kw, pageNum, append = false) => {
    if (loadingMore.current && append) return;
    loadingMore.current = append;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/getPharmacies`, {
        params: { page: pageNum, size: PAGE_SIZE, keyword: kw },
      });
      const formatted = (data.content || []).map(p => {
        dataMap.current[p.id] = p;
        return { value: p.id, label: p.city ? `${p.name}, ${p.city}` : p.name, icon: <PharmacyAvatar pharmacy={p} size={28} /> };
      });
      setOptions(prev => append ? [...prev, ...formatted] : formatted);
      setHasMore(!data.last);
    } catch (e) {
      console.error('Failed to fetch pharmacies', e);
    } finally {
      setLoading(false);
      loadingMore.current = false;
    }
  };

  const handleChange = (opt) => {
    onChange?.(opt);
    if (opt?.value) {
      onSelectPharmacy?.(dataMap.current[opt.value] ?? null);
    }
  };

  // Initial load
  useEffect(() => { fetchPage('', 0, false); }, []);

  // Initialize display when component has a pre-filled value
  useEffect(() => {
    const id = getValues(name);
    if (!id) return;
    axios.get(`${API_BASE_URL}/api/v1/pharmacies/${id}`, { withCredentials: true })
      .then(({ data }) => {
        dataMap.current[data.id] = data;
        const city = data.addresses?.[0]?.city;
        const opt = { value: data.id, label: city ? `${data.name}, ${city}` : data.name, icon: <PharmacyAvatar pharmacy={data} size={28} /> };
        inputRef.current?.setExternalValue(opt);
        setOptions(prev => prev.some(p => p.value === opt.value) ? prev : [opt, ...prev]);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSearch = useMemo(() =>
    debounce((kw) => {
      setKeyword(kw);
      setPage(0);
      fetchPage(kw, 0, false);
    }, 300),
  []);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    fetchPage(keyword, next, true);
  };

  // A brand-new pharmacy has no city/address yet — that's collected right after by the
  // register form itself (see Register.js's PharmacyAutofill), so onSelectPharmacy still
  // fires here (with an empty address), same as picking any other pharmacy.
  const handlePharmacyCreated = (created) => {
    dataMap.current[created.id] = created;
    const opt = {
      value: created.id,
      label: created.city ? `${created.name}, ${created.city}` : created.name,
      icon: <PharmacyAvatar pharmacy={created} size={28} />,
    };
    setOptions(prev => [opt, ...prev.filter(o => o.value !== opt.value)]);
    inputRef.current?.setExternalValue(opt);
    setShowCreate(false);
    handleChange(opt);
  };

  const createFooter = !disabled && (
    <button
      type="button"
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '8px 12px',
        background: '#f0fdf4', border: 'none', borderTop: '1px solid #e5e7eb',
        cursor: 'pointer', color: '#15803d', fontSize: 13, fontWeight: 500,
      }}
      onClick={() => setShowCreate(true)}
    >
      <FontAwesomeIcon icon={faPlus} />
      Créer{keyword ? ` "${keyword}"` : ' une nouvelle pharmacie'}
    </button>
  );

  return (
    <div className="position-relative">
      <ImprovedSelect
        ref={inputRef}
        name={name}
        multiple={false}
        options={options}
        onChange={handleChange}
        onInputChange={debouncedSearch}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        control={control}
        errors={errors}
        validationRules={validationRules}
        allowCustomValue={allowCustomValue}
        loading={loading}
        className={className}
        noOptionsFooter={createFooter}
        placeholder={t('input_pharmacy.placeholder')}
        disabled={disabled}
      />
      {showCreate && (
        <CreatePharmacyModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={handlePharmacyCreated}
          defaultName={keyword}
        />
      )}
    </div>
  );
};

export default InputPharmacy;
